import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Hourglass, Flag } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { dateAtAge, fmtDate, pension } from "@/lib/fers";
import { scanRetirementDates } from "@/lib/retireDate";
import { retirementDay } from "@/lib/snapshot";

const DAY = 864e5;
export const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

function useMilestoneAlerts(list) {
  const [on, setOn] = useState(null);
  const [server, setServer] = useState(null);
  useEffect(() => { api.get("/alerts/preferences").then((r) => { setOn(r.data.milestone_alerts); setServer(JSON.stringify(r.data.milestones)); }).catch(() => {}); }, []);
  const payload = JSON.stringify(list);
  useEffect(() => {
    if (server == null || server === payload) return;
    api.put("/alerts/milestones", { milestones: list }).then(() => setServer(payload)).catch(() => {});
  }, [server, payload]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (v) => {
    setOn(v);
    try { await api.put("/alerts/preferences", { milestone_alerts: v }); toast.success(v ? "Milestone emails on: 90 and 30 days before each date" : "Milestone emails off"); } catch (e) { setOn(!v); toast.error(errMsg(e)); }
  };
  return { on, toggle };
}

export { retirementDay };

function Unit({ v, l, testid }) {
  return <div className="text-center min-w-[64px]"><p className="font-mono-num text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-white tabular-nums" data-testid={testid}>{String(v).padStart(2, "0")}</p><p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{l}</p></div>;
}

export function RetirementCountdown({ profile }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const target = useMemo(() => (profile ? retirementDay(profile) : null), [profile]);
  const { next, list } = useMemo(() => {
    if (!profile) return { next: null, list: [] };
    const today = new Date();
    const future = scanRetirementDates(profile).milestones.map((m) => ({ ...m, when: m.exact || m.row.date })).filter((m) => m.when > today).sort((a, b) => a.when - b.when);
    const t = retirementDay(profile);
    const all = [...(t > today ? [{ key: "retirement-day", label: `Your retirement day (age ${profile.retire_age})`, date: iso(t) }] : []), ...future.map((m) => ({ key: slug(m.label), label: m.label, date: iso(m.when) }))];
    return { next: future[0] || null, list: all.slice(0, 15) };
  }, [profile]);
  const alerts = useMilestoneAlerts(list);
  if (!profile || !target) return null;
  const ms = Math.max(0, target - now);
  const d = Math.floor(ms / DAY), h = Math.floor((ms % DAY) / 36e5), m = Math.floor((ms % 36e5) / 6e4), s = Math.floor((ms % 6e4) / 1e3);
  return (
    <div className="glass p-5 mb-6 fade-up grid gap-5 lg:grid-cols-[1fr_auto_1fr] items-center" data-testid="retirement-countdown">
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Hourglass size={14} className="text-emerald-600" />Countdown to retirement</p>
        <p className="font-display text-xl font-semibold mt-1" data-testid="countdown-target-date">{fmtDate(target)}</p>
        <p className="text-xs text-muted-foreground">{profile.birth_date ? `Your ${profile.retire_age}th birthday` : `End of the year you turn ${profile.retire_age}. Add your birth date for the exact day.`}</p>
        {profile.early_out?.type && Number(profile.early_out.age) === Number(profile.retire_age) && (() => {
          const pen = pension(profile);
          const start = pen.startAge > profile.retire_age + 0.01 ? dateAtAge(profile, pen.startAge) : null;
          return <p className="text-xs mt-1 text-emerald-700 dark:text-emerald-400" data-testid="countdown-early-out">Plan: {({ regular: "Regular", vera: "VERA", vsip: "VSIP", mra10: "MRA+10" })[profile.early_out.type]} at {profile.retire_age}{pen.startAge > profile.retire_age + 0.01 ? ` · pension starts ${start ? fmtDate(start) : `at age ${pen.startAge}`}` : ""}</p>;
        })()}
        {alerts.on != null && (
          <label className="flex items-center gap-2 mt-3 text-xs cursor-pointer" data-testid="milestone-alerts-toggle">
            <Switch checked={alerts.on} onCheckedChange={alerts.toggle} data-testid="milestone-alerts-switch" />Email me 90 and 30 days before each milestone
          </label>
        )}
      </div>
      {ms > 0 ? (
        <div className="flex gap-2 sm:gap-4" data-testid="countdown-clock">
          <Unit v={d.toLocaleString()} l="days" testid="countdown-days" /><Unit v={h} l="hours" testid="countdown-hours" /><Unit v={m} l="min" testid="countdown-minutes" /><Unit v={s} l="sec" testid="countdown-seconds" />
        </div>
      ) : <p className="font-display text-2xl font-semibold text-grad-money" data-testid="countdown-done">You've reached your retirement day</p>}
      <div className="lg:text-right" data-testid="countdown-next-milestone">
        {next ? <>
          <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 lg:justify-end"><Flag size={14} className="text-cyan-600" />Next milestone</p>
          <p className="text-sm font-medium mt-1" data-testid="countdown-milestone-label">{next.label}</p>
          <p className="text-xs text-muted-foreground" data-testid="countdown-milestone-date">{fmtDate(next.when)} · {Math.ceil((next.when - now) / DAY).toLocaleString()} days to go</p>
        </> : <p className="text-sm text-muted-foreground">No upcoming service milestones</p>}
        <div className="flex gap-3 lg:justify-end"><Link to="/checklist" className="text-xs text-cyan-700 dark:text-cyan-400 underline" data-testid="countdown-checklist-link">Retirement checklist</Link><Link to="/best-date" className="text-xs text-cyan-700 dark:text-cyan-400 underline" data-testid="countdown-best-date-link">Best retirement date</Link></div>
      </div>
    </div>
  );
}
